import { useEffect, useState } from 'react';
import { InventoryAsset, OrderRequestSchema, Product } from '../../../types';
import { getProductSubject, useProductsStore } from '../../../store/Product';
import { getInventorySubject, useInventory } from '../../../store/Inventory';
import { getOrderRequestSchemaSubject, useOrderRequestSchemasStore } from '../../../store/OrderRequestSchema';
import {
  getPreferencesByKey,
  getSelectedAccountsSubject,
  getUserPreferencesSubject,
  OrderRequestPreferences
} from '../../../store/User';

const defaultPreferences: OrderRequestPreferences = {
  disabledProductFamilies: [],
  disabledProducts: {},
  displayProductFamilyName: true,
  isAdvancedForm: false,
  displayLocationCode: false,
  advancedNewLocationSettings: false
};

/** The products, inventory, order-request schemas and preferences the form reads from the stores, reloaded when the account scope changes. */
export function useOrderFormData() {
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [existingInventory, setExistingInventory] = useState<InventoryAsset[]>([]);
  const [orderRequestSchemas, setOrderRequestSchemas] = useState<OrderRequestSchema[]>([]);
  const [orderRequestPrefs, setOrderRequestPrefs] = useState<OrderRequestPreferences>(
    () => (getPreferencesByKey('order_request') as OrderRequestPreferences | undefined) ?? defaultPreferences
  );

  const { fetchOrderRequestSchemas } = useOrderRequestSchemasStore();
  const { fetchInventory } = useInventory();
  const { fetchProducts } = useProductsStore();

  useEffect(() => {
    const productSub = getProductSubject().subscribe(setAllProducts);
    const inventorySub = getInventorySubject().subscribe(setExistingInventory);

    const settingsSub = getUserPreferencesSubject().subscribe(() => {
      const prefs = getPreferencesByKey('order_request') as OrderRequestPreferences;
      if (prefs) setOrderRequestPrefs(prefs);
    });

    const schemasSub = getOrderRequestSchemaSubject().subscribe(setOrderRequestSchemas);

    // A BehaviorSubject fires immediately on subscribe and again on every change, so this
    // both loads data on mount and re-loads when the account scope is set after mount
    // (e.g. a direct Orders-page load that races ahead of scope). Both fetches skip while loading or
    // once loaded, so running them on every emission is safe.
    const accountsSub = getSelectedAccountsSubject().subscribe(() => {
      fetchInventory();
      fetchProducts();
    });

    fetchOrderRequestSchemas();

    return () => {
      productSub.unsubscribe();
      inventorySub.unsubscribe();
      settingsSub.unsubscribe();
      schemasSub.unsubscribe();
      accountsSub.unsubscribe();
    };
  }, []);

  return { allProducts, existingInventory, orderRequestSchemas, orderRequestPrefs };
}
